export type EstimateVariant = "A" | "B" | "C" | "D";

export const ESTIMATE_AB_TEST_KEY = "estimate-v1";

/**
 * Deterministically assigns an A/B/C/D variant from a session ID + test key.
 * Uses a simple djb2-style hash mod 4 so the same session always gets the same variant.
 * No database write is needed — the assignment is fully derived from the session ID.
 *
 * @param sessionId - The booking funnel session ID (uuid).
 * @param testKey   - Logical name for this experiment. Defaults to ESTIMATE_AB_TEST_KEY.
 *                    Changing this key rotates all sessions into new variant buckets.
 */

/**
 * All four possible variants in canonical order.
 * Used to validate and build the enabled-variant pool.
 */
export const ALL_ESTIMATE_VARIANTS: EstimateVariant[] = ["A", "B", "C", "D"];

export function getEstimateVariant(
  sessionId: string,
  testKey: string = ESTIMATE_AB_TEST_KEY,
  enabledVariants?: EstimateVariant[],
): EstimateVariant {
  // If a pool is provided, restrict assignment to only those variants.
  // Fall back to the full set if the pool is empty (safety net).
  const pool = enabledVariants && enabledVariants.length > 0
    ? enabledVariants
    : ALL_ESTIMATE_VARIANTS;

  const str = `${testKey}:${sessionId}`;
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
    hash = hash >>> 0;
  }
  const index = hash % pool.length;
  return pool[index];
}

/**
 * Simplified price ranges for Variant C — single-question flow.
 */
export const PRICE_RANGE_BUCKETS = [
  { id: "under150",   label: "Under $150",    min: 0,    max: 150,  description: "Small, simple piece" },
  { id: "150to300",   label: "$150 – $300",   min: 150,  max: 300,  description: "Small-to-medium, moderate detail" },
  { id: "300to600",   label: "$300 – $600",   min: 300,  max: 600,  description: "Medium piece or detailed work" },
  { id: "600to1000",  label: "$600 – $1,000", min: 600,  max: 1000, description: "Large or complex piece" },
  { id: "over1000",   label: "$1,000+",       min: 1000, max: 5000, description: "Full sleeve, back piece, or multi-session" },
] as const;

export type PriceRangeBucketId = typeof PRICE_RANGE_BUCKETS[number]["id"];
